import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useAuthStore, useAppStore } from '@indipia/shared';
import { photoApi } from '../api/photoApi';
import type { PhotoDetailResponse } from '../api/photoApi';
import { Button, KeywordForm, Modal, Textarea, ScrollTopButton } from '@indipia/ui-core';
import { ThumbImg } from '../components/ThumbImg';
import { usePhotoCommonStore } from '../store/photoCommonStore';
import { usePhotoNavStore } from '../store/photoNavStore';
import { useTagCodeMaps } from '../utils/tagCodeMap';

const BASE = '/agents/photo-organizer';

export interface PhotoNavItem {
  photoId: number;
  thumbUrl: string | null;
  originalUrl: string | null;
}

export interface SearchConditionChip {
  type: 'space' | 'stage' | 'work' | 'date' | 'keyword';
  label: string;
}

export interface PhotoDetailContext {
  photos: PhotoNavItem[];
  initialIndex: number;
  siteId?: number | null;
  siteName?: string | null;
  address?: string | null;
  searchConditions?: SearchConditionChip[];
}

const MOCK_NAV: PhotoNavItem[] = [
  { photoId: 1, thumbUrl: null, originalUrl: null },
];

function makeMockDetail(nav: PhotoNavItem): PhotoDetailResponse {
  return {
    photoId: nav.photoId,
    originalUrl: nav.originalUrl,
    thumbUrl: nav.thumbUrl,
    takenAt: '2026-05-25T10:18:00',
    originalFileName: `IMG_20260525_10${String(nav.photoId).padStart(2, '0')}.jpg`,
    width: null,
    height: null,
    fileSize: 4300000,
    gpsLat: 37.5612,
    gpsLng: 126.8259,
    siteName: '레미안 피스티지 84A',
    siteId: 1,
    siteAddress: '서울 강서구 마곡중앙로 12',
    memo: null,
    tags: [],
  };
}


function fmtDatetime(dateStr: string | null): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}  ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const IcnHome = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);
const IcnPin = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
  </svg>
);
const IcnCal = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const IcnFile = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
  </svg>
);

export function PhotoDetailMain() {
  const navigate = useNavigate();
  const context = usePhotoNavStore(s => s.photoDetailCtx);
  const user = useAuthStore(s => s.user);
  const [photos, setPhotos] = useState<PhotoNavItem[]>(() => context?.photos.length ? context.photos : MOCK_NAV);
  const [currentIdx, setCurrentIdx] = useState(context?.initialIndex ?? 0);
  const [detail, setDetail] = useState<PhotoDetailResponse | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [memo, setMemo] = useState('');
  const [savedMemo, setSavedMemo] = useState('');
  const [isEditingMemo, setIsEditingMemo] = useState(false);
  const [memoSaving, setMemoSaving] = useState(false);
  const [spaceTags, setSpaceTags] = useState<string[]>([]);
  const [workTags, setWorkTags] = useState<string[]>([]);
  const [userTags, setUserTags] = useState<string[]>([]);

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo(0, 0);
    (document.querySelector('main') as HTMLElement | null)?.scrollTo(0, 0);
  }, []);

  const commonCodes = usePhotoCommonStore(s => s.commonCodes);
  const userTagOptions = usePhotoCommonStore(s => s.userTags);
  const appCodes = useAppStore(s => s.codes);
  const { spaceCodeByName, workCodeByName } = useTagCodeMaps();
  const userTagIdByName = useMemo(() => Object.fromEntries(
    userTagOptions.map(u => [u.tagValue, u.userTagId])
  ), [userTagOptions]);

  const tagOptions: Record<'SPACE' | 'WORK' | 'USER', string[]> = {
    SPACE: (commonCodes?.['A01_SPACE_TAG'] ?? []).map(c => c.codeName),
    WORK: appCodes.filter(c => c.codeGroupCode === 'ZEU_WORK_TYPE').map(c => c.codeName),
    USER: userTagOptions.map(u => u.tagValue),
  };

  const idx = Math.min(currentIdx, photos.length - 1);
  const navPhoto = photos[idx];

  useEffect(() => {
    if (!navPhoto || !user) return;
    setLoadingDetail(true);

    const controller = new AbortController();
    photoApi
      .getPhotoDetail(navPhoto.photoId, controller.signal)
      .then(res => {
        const d = res.data?.data;
        if (d) {
          setDetail(d);
          setMemo(d.memo ?? '');
          setSavedMemo(d.memo ?? '');
        } else {
          setDetail(makeMockDetail(navPhoto));
          setMemo('');
          setSavedMemo('');
        }
        setIsEditingMemo(false);
      })
      .catch(err => {
        if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
          setDetail(makeMockDetail(navPhoto));
          setMemo('');
          setSavedMemo('');
          setIsEditingMemo(false);
        }
      })
      .finally(() => setLoadingDetail(false));

    return () => controller.abort();
  }, [navPhoto?.photoId, user]);

  useEffect(() => {
    if (!detail) return;
    setSpaceTags(detail.tags.filter(t => t.tagType === 'SPACE').map(t => t.tagValue));
    setWorkTags(detail.tags.filter(t => t.tagType === 'STAGE' || t.tagType === 'WORK_TYPE').map(t => t.tagValue));
    setUserTags(detail.tags.filter(t => t.tagType === 'CUSTOM' || t.tagType === 'USER').map(t => t.tagValue));
  }, [detail]);

  const handleSaveMemo = async () => {
    if (!navPhoto || !user) return;
    setMemoSaving(true);
    try {
      await photoApi.savePhotoMemo(navPhoto.photoId, memo);
      setSavedMemo(memo);
      setIsEditingMemo(false);
      toast.success('메모가 저장되었습니다');
    } catch {
      toast.error('메모 저장에 실패했습니다');
    } finally {
      setMemoSaving(false);
    }
  };

  const handleCancelMemo = () => {
    setMemo(savedMemo);
    setIsEditingMemo(false);
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMoveUnclassifiedConfirm, setShowMoveUnclassifiedConfirm] = useState(false);

  const handleDelete = () => {
    if (!navPhoto || !user) return;
    setShowDeleteConfirm(true);
  };

  const confirmMoveToUnclassified = async () => {
    if (!navPhoto) return;
    setShowMoveUnclassifiedConfirm(false);
    try {
      await photoApi.assignSite([{ photoId: navPhoto.photoId, siteId: null }]);
      toast.success('미분류로 이동되었습니다');
      const nextPhotos = photos.filter(p => p.photoId !== navPhoto.photoId);
      if (nextPhotos.length === 0) {
        navigate(-1);
      } else {
        setPhotos(nextPhotos);
        setCurrentIdx(i => Math.min(i, nextPhotos.length - 1));
      }
    } catch {
      toast.error('이동에 실패했습니다');
    }
  };

  const confirmDelete = async () => {
    if (!navPhoto) return;
    setShowDeleteConfirm(false);
    try {
      await photoApi.deletePhoto(navPhoto.photoId);
      toast.success('사진이 삭제되었습니다');
      const nextPhotos = photos.filter(p => p.photoId !== navPhoto.photoId);
      if (nextPhotos.length === 0) {
        context?.siteId ? navigate(`${BASE}/site/${context.siteId}`) : navigate(`${BASE}/site-list`);
      } else {
        setPhotos(nextPhotos);
        setCurrentIdx(i => Math.min(i, nextPhotos.length - 1));
      }
    } catch {
      toast.error('삭제에 실패했습니다');
    }
  };

  const d = detail ?? makeMockDetail(navPhoto ?? MOCK_NAV[0]);
  const imgSrc = d.originalUrl ?? d.thumbUrl ?? null;

  type InfoRow = { icon: React.ReactNode; label: string; value: string; mono?: boolean };
  const infoRows: InfoRow[] = [
    { icon: <IcnHome />, label: '현장',     value: context?.siteName ?? '-' },
    { icon: <IcnPin />,  label: '주소',     value: context?.address ?? '-' },
    { icon: <IcnCal />,  label: '촬영 일시', value: fmtDatetime(d.takenAt) },
    { icon: <IcnFile />, label: '파일명',   value: d.originalFileName ?? '-', mono: true },
{ icon: <IcnPin />, label: 'GPS',
      value: d.gpsLat && d.gpsLng
        ? `${d.gpsLat.toFixed(4)}, ${d.gpsLng.toFixed(4)}`
        : '-',
    },
  ];

  const conditions = context?.searchConditions ?? [];

  const buildTagPayload = (sp: string[], wk: string[], us: string[]) => [
    ...sp.map(v => ({ tagType: 'SPACE', tagValue: v, tagCode: spaceCodeByName[v] })),
    ...wk.map(v => ({ tagType: 'WORK_TYPE', tagValue: v, tagCode: workCodeByName[v] })),
    ...us.map(v => ({ tagType: 'CUSTOM', tagValue: v, tagCode: userTagIdByName[v] != null ? String(userTagIdByName[v]) : undefined })),
  ];

  const totalTagCount = spaceTags.length + workTags.length + userTags.length;

  type TagType = 'SPACE' | 'WORK' | 'USER';
  const tagsByType: Record<TagType, string[]> = { SPACE: spaceTags, WORK: workTags, USER: userTags };
  const setTagsByType: Record<TagType, (v: string[]) => void> = { SPACE: setSpaceTags, WORK: setWorkTags, USER: setUserTags };

  // 한 줄(공간·공종·나만의 태그)을 바꿔 낙관적 반영 후 저장, 실패 시 그 줄만 롤백
  const updateTags = (type: TagType, next: string[], errMsg: string) => {
    if (!navPhoto) return;
    const snap = tagsByType[type];
    setTagsByType[type](next);
    const all = { ...tagsByType, [type]: next };
    saveTagsToApi(navPhoto.photoId, all.SPACE, all.WORK, all.USER, () => setTagsByType[type](snap), errMsg);
  };

  const saveTagsToApi = async (
    photoId: number,
    newSp: string[], newWk: string[], newUs: string[],
    rollback: () => void,
    errMsg: string,
  ) => {
    try {
      await photoApi.patchPhotoTags(photoId, buildTagPayload(newSp, newWk, newUs));
    } catch {
      rollback();
      toast.error(errMsg);
    }
  };

  type TagRow = { type: TagType; icon: React.ReactNode; label: string };
  const tagRows: TagRow[] = [
    {
      type: 'SPACE',
      label: '공간',
      icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>,
    },
    {
      type: 'WORK',
      label: '공종',
      icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg>,
    },
    {
      type: 'USER',
      label: '나만의 태그',
      icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
    },
  ];

  /* 검색 조건 칩 아이콘 */
  const condIcon = (type: string) => {
    if (type === 'space') return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>;
    if (type === 'stage') return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /></svg>;
    if (type === 'work')  return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg>;
    return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>;
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, background: 'var(--shell-bg)', fontFamily: "-apple-system,'Apple SD Gothic Neo','Noto Sans KR',sans-serif" }}>

      {/* ── 상단 바 (뒤로가기 + 검색 조건) ── */}
      <div style={{ flexShrink: 0, background: 'var(--shell-surface)', borderBottom: '1px solid var(--shell-border)', padding: '9px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button variant="etc" size="sm" className="shrink-0" onClick={() => navigate(-1)}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6" /></svg>
          뒤로
        </Button>
        {conditions.length > 0 && (
          <>
            <div style={{ width: 1, height: 18, background: 'var(--shell-border)', flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: 'var(--shell-hint)', fontWeight: 600, flexShrink: 0 }}>검색 조건</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const }}>
              {conditions.map((c, i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 'var(--radius-full)', background: 'color-mix(in srgb, var(--gt-primary) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--gt-primary) 30%, transparent)', fontSize: 12, fontWeight: 600, color: 'var(--gt-primary)' }}>
                  {condIcon(c.type)}
                  {c.label}
                </span>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── 본문 (좌+우) ── */}
      <div ref={scrollRef} style={{ flex: 1, display: 'flex', overflowY: 'auto', minHeight: 0, alignItems: 'flex-start' }}>
        {/* 스크롤 맨 위로(D-09 §15.55) — 이 div 가 스크롤 소유자라 main 의 버튼이 반응하지 않는다. 직속 자식이라 스스로 이 div 를 찾는다 */}
        <ScrollTopButton />

      {/* ── 좌 패널 ── */}
      <div style={{ flex: 1, padding: '32px 24px 64px 80px', display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>

        {/* 사진 뷰어 카드 */}
        <div style={{ background: 'var(--shell-surface)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--shell-border)', boxShadow: '0 2px 16px var(--alpha-black-50)', flexShrink: 0 }}>

          {/* 사진 영역 */}
          <div style={{ position: 'relative', height: 400, background: 'var(--gray-900)', overflow: 'hidden' }}>
            <ThumbImg
              key={imgSrc}
              url={imgSrc}
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              fallback={
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="var(--alpha-white-300)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />
                  </svg>
                </div>
              }
            />

            {loadingDetail && (
              <div style={{ position: 'absolute', inset: 0, background: 'var(--alpha-black-300)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--alpha-white-800)" strokeWidth="2" strokeLinecap="round" style={{ animation: 'spin 1s linear infinite' }}>
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
              </div>
            )}

            {/* 이전 버튼 */}
            <button
              onClick={() => idx > 0 && setCurrentIdx(i => Math.max(0, i - 1))}
              aria-label="이전 사진"
              style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: 'var(--radius-full)', background: 'var(--alpha-white-900)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: idx > 0 ? 'pointer' : 'default', boxShadow: '0 2px 8px var(--alpha-black-300)', transition: 'opacity .15s', opacity: idx > 0 ? 1 : 0.3 }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--shell-on-surface-2)" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>

            {/* 다음 버튼 */}
            <button
              onClick={() => idx < photos.length - 1 && setCurrentIdx(i => Math.min(photos.length - 1, i + 1))}
              aria-label="다음 사진"
              style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: 'var(--radius-full)', background: 'var(--alpha-white-900)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: idx < photos.length - 1 ? 'pointer' : 'default', boxShadow: '0 2px 8px var(--alpha-black-300)', transition: 'opacity .15s', opacity: idx < photos.length - 1 ? 1 : 0.3 }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--shell-on-surface-2)" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>

            {/* N/M 카운터 배지 */}
            <div style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', background: 'var(--alpha-black-700)', color: 'var(--gray-0)', fontSize: 12, fontWeight: 700, padding: '4px 13px', borderRadius: 'var(--radius-full)', letterSpacing: '-0.2px', backdropFilter: 'blur(4px)', whiteSpace: 'nowrap' as const }}>
              {idx + 1} / {photos.length}
            </div>

            {/* 현장 상세 + 미분류로 이동 버튼 */}
            <div style={{ position: 'absolute', bottom: 10, right: 52, display: 'flex', gap: 6 }}>
              <Button
                variant="etc"
                onClick={() => context?.siteId ? navigate(`${BASE}/site/${context.siteId}`) : navigate(`${BASE}/site-list`)}
                style={{ height: 'auto', padding: '6px 13px', border: 'none', background: 'var(--alpha-white-900)', fontSize: 12, backdropFilter: 'blur(4px)', boxShadow: '0 2px 8px var(--alpha-black-200)' }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M8 21v-6h8v6" /><path d="M3 9h18" />
                </svg>
                현장 상세
              </Button>
              <Button
                variant="etc"
                onClick={() => setShowMoveUnclassifiedConfirm(true)}
                style={{ height: 'auto', padding: '6px 13px', border: 'none', background: 'var(--alpha-white-900)', fontSize: 12, backdropFilter: 'blur(4px)', boxShadow: '0 2px 8px var(--alpha-black-200)' }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 14l-4-4 4-4" /><path d="M5 10h11a4 4 0 0 1 0 8h-1" />
                </svg>
                미분류로 이동
              </Button>
            </div>

            {/* 삭제 버튼 — 우측 하단 */}
            <button
              onClick={handleDelete}
              aria-label="사진 삭제"
              style={{ position: 'absolute', bottom: 10, right: 12, width: 32, height: 32, borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--color-error)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px color-mix(in srgb, var(--color-error) 45%, transparent)' }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gray-0)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
            </button>
          </div>

          {/* 파일 정보 바 — 한 줄 */}
          <div style={{ padding: '11px 16px 10px', borderBottom: '1px solid var(--shell-border)' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface)', letterSpacing: '-0.3px' }}>
              {d.originalFileName ?? 'IMG_unknown.jpg'}
            </span>
          </div>

          {/* 썸네일 스트립 */}
          <div style={{ padding: '10px 16px', display: 'flex', gap: 7, overflowX: 'auto' }}>
            {photos.map((p, i) => (
              <div
                key={p.photoId}
                role="button"
                tabIndex={0}
                onClick={() => setCurrentIdx(i)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCurrentIdx(i); } }}
                style={{
                  width: 50, height: 50, borderRadius: 'var(--radius-sm)', flexShrink: 0, overflow: 'hidden',
                  border: `2px solid ${i === idx ? 'var(--gt-primary)' : 'var(--shell-border)'}`,
                  background: 'var(--gt-subtle)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'border-color .12s',
                  boxSizing: 'border-box' as const,
                }}
              >
                <ThumbImg
                  url={p.thumbUrl ?? p.originalUrl ?? null}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  fallback={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={i === idx ? 'var(--gt-primary)' : 'var(--shell-hint)'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />
                    </svg>
                  }
                />
              </div>
            ))}
          </div>
        </div>

        {/* 사진 메모 카드 */}
        <div style={{ background: 'var(--shell-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--shell-border)', boxShadow: '0 2px 10px var(--alpha-black-50)', padding: '15px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
            <div style={{ width: 26, height: 26, borderRadius: 'var(--radius-sm)', background: 'var(--gt-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
            </div>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--shell-on-surface)', letterSpacing: '-0.3px' }}>사진 메모</span>
          </div>
          {savedMemo && !isEditingMemo ? (
            <>
              <p style={{
                margin: '0 0 10px', padding: '10px 12px',
                border: '1px solid var(--shell-border)', borderRadius: 'var(--radius-md)', background: 'var(--shell-surface)',
                fontSize: 12, color: 'var(--shell-on-surface)', lineHeight: 1.75,
                whiteSpace: 'pre-wrap' as const, wordBreak: 'break-word' as const,
                maxHeight: 140, overflowY: 'auto' as const,
              }}>
                {savedMemo}
              </p>
              <Button variant="secondary" className="w-full" onClick={() => setIsEditingMemo(true)}>
                수정
              </Button>
            </>
          ) : (
            <>
              <div style={{ marginBottom: 10 }}>
                <Textarea
                  fill
                  value={memo}
                  onChange={e => setMemo(e.target.value)}
                  maxLength={2000}
                  placeholder={'이 사진에 대한 메모를 남겨두세요\n(선택사항)'}
                  style={{ maxHeight: 140 }}
                />
                <div style={{ textAlign: 'right', marginTop: 4 }}>
                  <span style={{ fontSize: 12, color: memo.length >= 1900 ? 'var(--gt-primary)' : 'var(--shell-hint)' }}>
                    {memo.length.toLocaleString()} / 2,000
                  </span>
                </div>
              </div>
              {isEditingMemo ? (
                <div style={{ display: 'flex', gap: 7 }}>
                  <Button variant="primary" className="flex-1" loading={memoSaving} onClick={handleSaveMemo}>
                    저장
                  </Button>
                  <Button variant="etc" className="flex-1" disabled={memoSaving} onClick={handleCancelMemo}>
                    취소
                  </Button>
                </div>
              ) : (
                <Button variant="primary" className="w-full" loading={memoSaving} onClick={handleSaveMemo}>
                  메모 저장
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── 우 패널 ── */}
      <div style={{ width: 'min(284px, 28vw)', background: 'var(--shell-bg)', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12, padding: '32px 24px 64px 16px', position: 'sticky', top: 0, alignSelf: 'flex-start' }}>

        {/* 분류 태그 카드 */}
        <div style={{ background: 'var(--shell-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--shell-border)', boxShadow: '0 2px 10px var(--alpha-black-50)', padding: '15px 16px 12px' }}>
          {/* 헤더 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14 }}>
            <div style={{ width: 26, height: 26, borderRadius: 'var(--radius-sm)', background: 'var(--gt-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
            </div>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--shell-on-surface)', letterSpacing: '-0.3px' }}>분류 태그</span>
          </div>

          {/* 태그 행들 */}
          {totalTagCount >= 10 && (
            <span style={{ display: 'block', fontSize: 12, color: 'var(--gt-primary)', marginBottom: 12 }}>태그는 최대 10개까지 등록 가능합니다.</span>
          )}
          {tagRows.map((row, ri) => (
            <div key={row.type} style={{ marginBottom: ri < tagRows.length - 1 ? 12 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
                {row.icon}
                <span style={{ fontSize: 12, color: 'var(--shell-hint)', fontWeight: 600 }}>{row.label}</span>
              </div>
              {/* 공간·공종은 공통코드 목록에서만, 나만의 태그는 직접 입력 + 목록 추천 */}
              <KeywordForm
                tags={tagsByType[row.type]}
                options={tagOptions[row.type]}
                allowCustom={row.type === 'USER'}
                maxLength={10}
                placeholder={row.type === 'USER' ? '태그 입력 후 엔터' : `${row.label} 선택`}
                onAdd={v => {
                  // 10개 제한은 세 줄 합계 기준이라 줄 단위 maxCount 대신 여기서 막는다
                  if (totalTagCount >= 10) { toast.error('태그는 최대 10개까지 등록 가능합니다'); return; }
                  updateTags(row.type, [...tagsByType[row.type], v], '태그 저장에 실패했습니다');
                }}
                onRemove={i => updateTags(row.type, tagsByType[row.type].filter((_, j) => j !== i), '태그 삭제에 실패했습니다')}
              />
            </div>
          ))}
        </div>

        {/* 사진 정보 카드 */}
        <div style={{ background: 'var(--shell-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--shell-border)', boxShadow: '0 2px 10px var(--alpha-black-50)', padding: '15px 16px 4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 13 }}>
            <div style={{ width: 26, height: 26, borderRadius: 'var(--radius-sm)', background: 'var(--shell-skeleton)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--shell-on-surface)', letterSpacing: '-0.3px' }}>사진 정보</span>
          </div>

          {infoRows.map((row, i) => (
            <div
              key={i}
              style={{
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8,
                paddingBottom: 11, marginBottom: 11,
                borderBottom: i < infoRows.length - 1 ? '1px solid var(--shell-border)' : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, paddingTop: 1 }}>
                {row.icon}
                <span style={{ fontSize: 12, color: 'var(--shell-hint)', fontWeight: 500, whiteSpace: 'nowrap' as const }}>{row.label}</span>
              </div>
              <span style={{
                fontSize: row.mono ? 10.5 : 11.5,
                fontWeight: row.mono ? 500 : 600,
                color: row.mono ? 'var(--gt-primary)' : 'var(--shell-on-surface)',
                textAlign: 'right' as const,
                wordBreak: 'break-all' as const,
                fontFamily: row.mono ? "'Courier New', monospace" : 'inherit',
                lineHeight: 1.55,
              }}>
                {row.value}
              </span>
            </div>
          ))}
        </div>

      </div>

      </div>{/* 본문 끝 */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* 미분류로 이동 확인 모달 */}
      <Modal open={showMoveUnclassifiedConfirm} onClose={() => setShowMoveUnclassifiedConfirm(false)}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '10px 4px' }}>
          <div style={{ width: 52, height: 52, borderRadius: 'var(--radius-xl)', background: 'color-mix(in srgb, var(--shell-hint) 10%, transparent)', border: '1.5px solid color-mix(in srgb, var(--shell-hint) 25%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--shell-on-surface-2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 14l-4-4 4-4" /><path d="M5 10h11a4 4 0 0 1 0 8h-1" />
            </svg>
          </div>
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 'var(--type-heading-sm-size)', fontWeight: 700, color: 'var(--shell-on-surface)' }}>미분류로 이동하시겠습니까?</div>
            <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)', lineHeight: 1.6 }}>현장 배정이 해제되고 미분류 사진으로 이동합니다.</div>
          </div>
          <div style={{ display: 'flex', gap: 8, width: '100%', marginTop: 4 }}>
            <Button variant="etc" className="flex-1" onClick={() => setShowMoveUnclassifiedConfirm(false)}>취소</Button>
            <Button variant="primary" className="flex-1" onClick={confirmMoveToUnclassified}>이동</Button>
          </div>
        </div>
      </Modal>

      {/* 삭제 확인 모달 */}
      <Modal open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '10px 4px' }}>
          <div style={{ width: 52, height: 52, borderRadius: 'var(--radius-xl)', background: 'color-mix(in srgb, var(--color-error) 8%, transparent)', border: '1.5px solid color-mix(in srgb, var(--color-error) 25%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-error)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </div>
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 'var(--type-heading-sm-size)', fontWeight: 700, color: 'var(--shell-on-surface)' }}>사진을 삭제하시겠습니까?</div>
            <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)', lineHeight: 1.6 }}>삭제된 사진은 복구할 수 없습니다.</div>
          </div>
          <div style={{ display: 'flex', gap: 8, width: '100%', marginTop: 4 }}>
            <Button variant="primary" className="flex-1" onClick={() => setShowDeleteConfirm(false)}>취소</Button>
            <Button variant="etc" className="flex-1" onClick={confirmDelete}>삭제</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
